import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { shippingAPI, documentsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { LuTruck, LuDownload, LuPackageCheck, LuMapPin, LuRefreshCw, LuExternalLink, LuPencil, LuChevronLeft, LuChevronRight, LuCheck, LuX } from 'react-icons/lu';
import LoadingSpinner from './LoadingSpinner';
import PackageEditor from './PackageEditor';

/**
 * Normalize a raw rate object from the API into a consistent shape.
 */
function normalizeRate(rate, index) {
  // Demo rates don't have IDs; generate one from carrier+service+index
  // so each rate is uniquely selectable per package.
  const id = rate.id || `demo-${(rate.carrier || '').toLowerCase().replace(/\s+/g, '-')}-${(rate.service || '').toLowerCase().replace(/\s+/g, '-')}-${index}`;
  return {
    id,
    carrier: rate.carrier,
    service: rate.service,
    label: rate.service_name || `${rate.carrier || ''} ${rate.service || ''}`.trim() || id,
    cost: Number(rate.cost ?? rate.estimatedCost ?? 0),
    transitDays: rate.transit_days || rate.transitDays || null,
    estimatedDeliveryDate: rate.estimated_delivery_date || rate.estimatedDeliveryDate || null
  };
}

/**
 * Shipment management panel for request detail pages.
 * Shows shipment info, label purchase/download, and tracking.
 *
 * Props:
 *   shipment - Shipment object (from request.Shipment or fetched separately)
 *   requestId - Request UUID (for creating shipments)
 *   onUpdate - callback when shipment is updated
 *   isAdmin - whether the user is an admin (shows purchase/refund actions)
 */
const ShipmentPanel = ({ shipment, requestId, requestShippingMethod = null, onUpdate, isAdmin = false }) => {
  const queryClient = useQueryClient();
  const [packageEditorOpen, setPackageEditorOpen] = useState(false);
  const [pickupModalOpen, setPickupModalOpen] = useState(false);
  const [pickupName, setPickupName] = useState('');
  const [pickupNotes, setPickupNotes] = useState('');
  const [signatureHasInk, setSignatureHasInk] = useState(false);
  const signatureCanvasRef = useRef(null);
  const signatureDrawingRef = useRef(false);
  const packages = useMemo(() => shipment?.Packages || [], [shipment?.Packages]);

  // Wizard state: idle | rating | selecting | review | purchasing | done
  const [wizardState, setWizardState] = useState('idle');
  const [wizardStep, setWizardStep] = useState(0);
  // { [packageId]: { rates: [], selectedRateId, easypostShipmentId } }
  const [packageRates, setPackageRates] = useState({});
  const [purchaseProgress, setPurchaseProgress] = useState({ current: 0, total: 0 });
  const [purchaseErrors, setPurchaseErrors] = useState([]);
  const isPickup = requestShippingMethod === 'pickup' || shipment?.Request?.shippingMethod === 'pickup';

  // Packages that still need labels
  const unlabeledPackages = useMemo(
    () => packages.filter((pkg) => !pkg.label_url),
    [packages]
  );

  const currentWizardPackage = unlabeledPackages[wizardStep] || null;

  // Fetch tracking info when shipment has a tracker
  const { data: trackingData } = useQuery(
    ['shipment-tracking', shipment?.id],
    () => shippingAPI.getTrackingInfo(shipment.id),
    {
      enabled: !!shipment?.easypost_tracker_id,
      refetchInterval: 60000
    }
  );

  const refundLabelMutation = useMutation(
    () => shippingAPI.refundLabel(shipment.id),
    {
      onSuccess: (data) => {
        toast.success(data.data?.message || 'Refund submitted');
        queryClient.invalidateQueries(['request']);
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to refund label');
      }
    }
  );

  const completePickupMutation = useMutation(
    async (payload) => {
      let signatureDocument = null;
      if (payload.signatureFile) {
        const uploadResponse = await documentsAPI.uploadDocument(
          requestId,
          payload.signatureFile,
          'other',
          `Pickup signature for shipment ${shipment.shipment_number || shipment.id}`
        );
        signatureDocument = uploadResponse?.data || null;
      }

      return shippingAPI.completePickupShipment(shipment.id, {
        pickedUpByName: payload.pickedUpByName,
        pickupSignatureName: payload.pickedUpByName,
        signatureDocumentId: signatureDocument?.id || null,
        signatureDocumentUrl: signatureDocument?.file_url || null,
        notes: payload.notes
      });
    },
    {
      onSuccess: () => {
        toast.success('Pickup completed');
        setPickupModalOpen(false);
        setPickupName('');
        setPickupNotes('');
        setSignatureHasInk(false);
        queryClient.invalidateQueries(['request']);
        if (onUpdate) onUpdate();
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to complete pickup');
      }
    }
  );

  const completeCarrierMutation = useMutation(
    (payload) => shippingAPI.completeCarrierShipment(shipment.id, payload),
    {
      onSuccess: () => {
        toast.success('Carrier shipment marked shipped');
        queryClient.invalidateQueries(['request']);
        if (onUpdate) onUpdate();
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to mark shipment shipped');
      }
    }
  );

  // Fetch rates for the current wizard package
  const fetchRatesForPackage = useCallback(async (pkg) => {
    setWizardState('rating');
    try {
      const rateResp = await shippingAPI.getPackageRates(shipment.id, pkg.id, requestShippingMethod);
      const rates = (rateResp?.rates || []).map(normalizeRate);
      setPackageRates((prev) => ({
        ...prev,
        [pkg.id]: {
          rates,
          selectedRateId: rates[0]?.id || null,
          easypostShipmentId: rateResp?.easypost_shipment_id || pkg.easypost_shipment_id || shipment.easypost_shipment_id
        }
      }));
      setWizardState('selecting');
    } catch (err) {
      toast.error(`Failed to fetch rates for ${pkg.package_label || `Package ${pkg.package_number}`}: ${err.response?.data?.error || err.message}`);
      setWizardState('idle');
    }
  }, [shipment]);

  // Advance to next package or review
  const goNextStep = useCallback(() => {
    const nextStep = wizardStep + 1;
    if (nextStep >= unlabeledPackages.length) {
      setWizardState('review');
    } else {
      setWizardStep(nextStep);
      // If we already have rates cached for this package, go straight to selecting
      if (packageRates[unlabeledPackages[nextStep].id]?.rates?.length > 0) {
        setWizardState('selecting');
      } else {
        fetchRatesForPackage(unlabeledPackages[nextStep]);
      }
    }
  }, [wizardStep, unlabeledPackages, packageRates, fetchRatesForPackage]);

  // Go back to previous package
  const goPrevStep = useCallback(() => {
    if (wizardStep > 0) {
      setWizardStep(wizardStep - 1);
      setWizardState('selecting');
    }
  }, [wizardStep]);

  // Purchase all labels sequentially
  const purchaseAllLabels = useCallback(async () => {
    setWizardState('purchasing');
    const total = unlabeledPackages.length;
    setPurchaseProgress({ current: 0, total });
    const errors = [];
    let successCount = 0;

    for (let i = 0; i < total; i++) {
      setPurchaseProgress({ current: i + 1, total });
      const pkg = unlabeledPackages[i];
      const pkgData = packageRates[pkg.id];
      if (!pkgData?.selectedRateId) {
        errors.push(`${pkg.package_label || `Package ${pkg.package_number}`}: no rate selected`);
        continue;
      }
      try {
        await shippingAPI.purchasePackageLabel(
          shipment.id,
          pkg.id,
          pkgData.selectedRateId,
          'PDF',
          pkgData.easypostShipmentId
        );
        successCount++;
      } catch (err) {
        errors.push(`${pkg.package_label || `Package ${pkg.package_number}`}: ${err.response?.data?.error || err.message}`);
      }
    }

    setPurchaseErrors(errors);

    if (successCount > 0) {
      toast.success(`Purchased ${successCount} label${successCount > 1 ? 's' : ''}`);
      queryClient.invalidateQueries(['request']);
      onUpdate?.();
    }
    if (errors.length > 0) {
      errors.forEach((msg) => toast.error(msg));
    }
    setWizardState('done');
  }, [unlabeledPackages, packageRates, shipment, queryClient, onUpdate]);

  const cancelWizard = useCallback(() => {
    setWizardState('idle');
    setWizardStep(0);
    setPackageRates({});
    setPurchaseErrors([]);
  }, []);

  const hasLabel = !!shipment?.label_url;
  const hasTracking = !!shipment?.tracking_number;
  const canRefundLabel = hasLabel && isAdmin && ['pending', 'picked', 'packed'].includes(shipment?.status);
  const canCompletePickup = isAdmin && isPickup && ['pending', 'picked', 'packed'].includes(shipment?.status);
  const canCompleteCarrier = isAdmin && !isPickup && ['pending', 'picked', 'packed'].includes(shipment?.status);
  const tracking = trackingData?.tracking;

  // Compute review totals
  const reviewTotal = unlabeledPackages.reduce((sum, pkg) => {
    const pkgData = packageRates[pkg.id];
    if (!pkgData?.selectedRateId) return sum;
    const rate = pkgData.rates.find((r) => r.id === pkgData.selectedRateId);
    return sum + (rate?.cost || 0);
  }, 0);

  // Helper to get the selected rate display for a package
  const getSelectedRateForPackage = (pkg) => {
    const pkgData = packageRates[pkg.id];
    if (!pkgData?.selectedRateId) return null;
    return pkgData.rates.find((r) => r.id === pkgData.selectedRateId) || null;
  };

  const pickupMetadata = isPickup
    ? packages.find((pkg) => pkg.metadata?.picked_up_by_name || pkg.metadata?.pickup_signature_name)?.metadata || {}
    : {};

  useEffect(() => {
    if (!pickupModalOpen) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.max(Math.floor(rect.width * ratio), 1);
      canvas.height = Math.max(Math.floor(rect.height * ratio), 1);
      const context = canvas.getContext('2d');
      context.scale(ratio, ratio);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, rect.width, rect.height);
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.lineWidth = 2.5;
      context.strokeStyle = '#111827';
    };

    resizeCanvas();
  }, [pickupModalOpen]);

  const getSignaturePoint = (event) => {
    const canvas = signatureCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const startSignature = (event) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    canvas.setPointerCapture?.(event.pointerId);
    const context = canvas.getContext('2d');
    const point = getSignaturePoint(event);
    signatureDrawingRef.current = true;
    context.beginPath();
    context.moveTo(point.x, point.y);
  };

  const drawSignature = (event) => {
    if (!signatureDrawingRef.current) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    const context = canvas.getContext('2d');
    const point = getSignaturePoint(event);
    context.lineTo(point.x, point.y);
    context.stroke();
    setSignatureHasInk(true);
  };

  const stopSignature = (event) => {
    signatureDrawingRef.current = false;
    signatureCanvasRef.current?.releasePointerCapture?.(event.pointerId);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, rect.width, rect.height);
    context.beginPath();
    setSignatureHasInk(false);
  };

  const canvasToFile = async () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    const safeShipmentNumber = (shipment.shipment_number || shipment.id || 'pickup').toString().replace(/[^a-zA-Z0-9-_]/g, '-');
    return new File([blob], `pickup-signature-${safeShipmentNumber}.png`, { type: 'image/png' });
  };

  const submitPickupCompletion = async () => {
    const trimmedName = pickupName.trim();
    if (!trimmedName) {
      toast.error('Picked up by name is required');
      return;
    }
    if (!signatureHasInk) {
      toast.error('Pickup signature is required');
      return;
    }
    const signatureFile = await canvasToFile();
    if (!signatureFile) {
      toast.error('Could not capture pickup signature');
      return;
    }
    completePickupMutation.mutate({
      pickedUpByName: trimmedName,
      notes: pickupNotes.trim() || null,
      signatureFile
    });
  };

  // Check if the current step has a valid selection (for Next button)
  const currentStepHasSelection = currentWizardPackage
    ? !!packageRates[currentWizardPackage.id]?.selectedRateId
    : false;

  if (!shipment) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center">
            <LuTruck className="h-4 w-4 mr-2 text-gray-500" />
            Shipment {shipment.shipment_number || ''}
          </h3>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            shipment.status === 'delivered' ? 'bg-green-100 text-green-800' :
            shipment.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
            shipment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {shipment.status}
          </span>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        {/* Carrier & Service */}
        {shipment.carrier && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Carrier</span>
            <span className="text-gray-900 font-medium">
              {isPickup ? 'Customer Pickup' : `${shipment.carrier} ${shipment.service_level || ''}`.trim()}
            </span>
          </div>
        )}

        {/* Shipping Cost */}
        {shipment.shipping_cost && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Cost</span>
            <span className="text-gray-900 font-medium">${parseFloat(shipment.shipping_cost).toFixed(2)}</span>
          </div>
        )}
        {isPickup && !shipment.shipping_cost && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Cost</span>
            <span className="text-gray-900 font-medium">$0.00</span>
          </div>
        )}

        {isPickup && shipment.status === 'shipped' && (pickupMetadata.picked_up_by_name || pickupMetadata.pickup_signature_name) && (
          <div className="rounded-md border border-green-100 bg-green-50 px-3 py-2 text-sm text-green-950">
            <p className="font-medium">Pickup Acknowledgement</p>
            {pickupMetadata.picked_up_by_name && (
              <p className="mt-1">Picked up by: {pickupMetadata.picked_up_by_name}</p>
            )}
            {pickupMetadata.pickup_signature_name && (
              <p>Signature: {pickupMetadata.pickup_signature_name}</p>
            )}
            {pickupMetadata.pickup_signature_captured_at && (
              <p className="text-xs text-green-800">
                Captured: {new Date(pickupMetadata.pickup_signature_captured_at).toLocaleString()}
              </p>
            )}
            {pickupMetadata.pickup_notes && (
              <p className="mt-1 text-xs text-green-800">Notes: {pickupMetadata.pickup_notes}</p>
            )}
          </div>
        )}

        {canCompletePickup && (
          <div className="pt-2 border-t border-gray-100">
            <button
              type="button"
              disabled={completePickupMutation.isLoading}
              onClick={() => setPickupModalOpen(true)}
              className="inline-flex items-center rounded-md border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {completePickupMutation.isLoading ? 'Completing...' : 'Mark Picked Up'}
            </button>
          </div>
        )}

        {pickupModalOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-3 py-4 sm:items-center">
            <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Complete Pickup</h3>
                  <p className="text-xs text-gray-500">Capture pickup acknowledgement before closing this shipment.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPickupModalOpen(false)}
                  className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <LuX className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4 px-4 py-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Picked Up By</label>
                  <input
                    type="text"
                    value={pickupName}
                    onChange={(event) => setPickupName(event.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 sm:text-sm"
                    placeholder="Customer or driver name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={pickupNotes}
                    onChange={(event) => setPickupNotes(event.target.value)}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 sm:text-sm"
                    placeholder="Optional pickup notes"
                  />
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">Signature</label>
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="text-xs font-medium text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  </div>
                  <canvas
                    ref={signatureCanvasRef}
                    onPointerDown={startSignature}
                    onPointerMove={drawSignature}
                    onPointerUp={stopSignature}
                    onPointerCancel={stopSignature}
                    className="h-44 w-full touch-none rounded-md border border-gray-300 bg-white"
                  />
                  <p className="mt-1 text-xs text-gray-500">Use a mouse, stylus, or finger on mobile.</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setPickupModalOpen(false)}
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={completePickupMutation.isLoading}
                  onClick={submitPickupCompletion}
                  className="rounded-md border border-green-700 bg-green-700 px-3 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {completePickupMutation.isLoading ? 'Completing...' : 'Complete Pickup'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tracking Number */}
        {hasTracking && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Tracking</span>
            <div className="flex items-center space-x-2">
              <span className="text-gray-900 font-mono text-xs">{shipment.tracking_number}</span>
              {shipment.public_tracking_url && (
                <a
                  href={shipment.public_tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  <LuExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Packages */}
        {packages.length > 0 && wizardState === 'idle' && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-gray-700">Packages</h4>
              {isAdmin && ['pending', 'picked', 'packed'].includes(shipment.status) && (
                <button
                  type="button"
                  onClick={() => setPackageEditorOpen(true)}
                  className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-800"
                >
                  <LuPencil className="h-3 w-3 mr-1" />
                  Edit Packages
                </button>
              )}
            </div>
            <div className="space-y-2">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-left"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{pkg.package_label || `Package ${pkg.package_number}`}</div>
                      <div className="text-xs text-gray-500">
                        {pkg.package_type || 'box'} -- {pkg.weight_lbs || 0} lbs -- {pkg.length || '-'} x {pkg.width || '-'} x {pkg.height || '-'} in
                      </div>
                      {pkg.tracking_number && (
                        <div className="mt-1 text-xs font-mono text-gray-700">Tracking: {pkg.tracking_number}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {pkg.label_url && (
                        <span className="inline-flex items-center text-xs text-green-700 font-medium">
                          <LuCheck className="h-3 w-3 mr-0.5" />
                          Label
                        </span>
                      )}
                      <span className="text-xs text-gray-500">{pkg.status}</span>
                    </div>
                  </div>
                  {pkg.label_url && (
                    <a
                      href={pkg.label_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      Download Package Label
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Estimated Delivery */}
        {shipment.estimated_delivery_date && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Est. Delivery</span>
            <span className="text-gray-900">{new Date(shipment.estimated_delivery_date).toLocaleDateString()}</span>
          </div>
        )}

        {/* Shipped / Delivered dates */}
        {shipment.shipped_at && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Shipped</span>
            <span className="text-gray-900">{new Date(shipment.shipped_at).toLocaleDateString()}</span>
          </div>
        )}
        {shipment.delivered_at && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Delivered</span>
            <span className="text-gray-900">{new Date(shipment.delivered_at).toLocaleDateString()}</span>
          </div>
        )}

        {/* Label Download */}
        {hasLabel && !isPickup && (
          <div className="pt-2 border-t border-gray-100">
            <a
              href={shipment.label_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
            >
              <LuDownload className="h-4 w-4 mr-1.5" />
              Download Label ({shipment.label_format || 'PDF'})
            </a>
          </div>
        )}

        {!isPickup && (canCompleteCarrier || !hasLabel) && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {!hasLabel && (
              <p>
                Book carrier service and purchase labels in the standard shipping portal. EasyPost label purchase is deferred in this app.
              </p>
            )}
            {hasLabel && canCompleteCarrier && (
              <p>
                Confirm the carrier shipment after labels are purchased and the package leaves the warehouse.
              </p>
            )}
            {canCompleteCarrier && (
              <button
                type="button"
                disabled={completeCarrierMutation.isLoading}
                onClick={() => {
                  const carrier = window.prompt('Carrier? Example: UPS, FedEx, DHL, Canada Post');
                  if (carrier === null) return;
                  const trackingNumber = window.prompt('Tracking number?');
                  if (trackingNumber === null) return;
                  const serviceLevel = window.prompt('Service level? Optional.');
                  if (serviceLevel === null) return;
                  const notes = window.prompt('Shipping notes? Optional.');
                  if (notes === null) return;

                  completeCarrierMutation.mutate({
                    carrier: carrier.trim(),
                    tracking_number: trackingNumber.trim(),
                    service_level: serviceLevel.trim() || null,
                    notes: notes.trim() || null
                  });
                }}
                className="mt-3 inline-flex items-center rounded-md border border-amber-700 bg-amber-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {completeCarrierMutation.isLoading ? 'Marking Shipped...' : 'Mark Shipped After Portal Booking'}
              </button>
            )}
          </div>
        )}

        {/* Picked Items */}
        {shipment.LineItems && shipment.LineItems.length > 0 && (
          <div className="pt-3 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Picked Items</h4>
            <div className="space-y-2">
              {shipment.LineItems.map((lineItem) => {
                const requestLine = lineItem.RequestLineItem || {};
                const inventoryItem = requestLine.InventoryItem || {};
                const assets = lineItem.Assets || [];
                return (
                  <div key={lineItem.id} className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {inventoryItem.product_sku || requestLine.productSku || 'Item'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Picked {lineItem.quantity_picked || 0} / {lineItem.quantity_to_ship || 0}
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">{lineItem.status || shipment.status}</span>
                    </div>
                    {assets.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {assets.map((assetLink) => {
                          const asset = assetLink.InventoryAsset || {};
                          const label = asset.serial_number
                            ? `SN ${asset.serial_number}`
                            : asset.mac_address
                              ? `MAC ${asset.mac_address}`
                              : `Asset ${String(asset.id || '').slice(0, 8)}`;
                          return (
                            <span key={assetLink.id} className="inline-flex rounded bg-white px-2 py-0.5 text-[11px] font-mono text-gray-700 border border-gray-200">
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === Rate Selection Wizard === */}

        {/* EasyPost label/rate purchase is deferred; carrier shipments use the standard shipping portal. */}

        {/* Rating: loading spinner while fetching rates */}
        {wizardState === 'rating' && (
          <div className="pt-3 border-t border-gray-100">
            <WizardHeader
              step={wizardStep}
              total={unlabeledPackages.length}
              pkg={currentWizardPackage}
              onCancel={cancelWizard}
            />
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
              <span className="text-sm text-gray-500 ml-2">Fetching rates...</span>
            </div>
          </div>
        )}

        {/* Selecting: show rate cards for the current package */}
        {wizardState === 'selecting' && currentWizardPackage && (
          <div className="pt-3 border-t border-gray-100">
            <WizardHeader
              step={wizardStep}
              total={unlabeledPackages.length}
              pkg={currentWizardPackage}
              onCancel={cancelWizard}
            />
            {(() => {
              const pkgData = packageRates[currentWizardPackage.id];
              const rates = pkgData?.rates || [];
              const selectedId = pkgData?.selectedRateId;

              if (rates.length === 0) {
                return (
                  <div className="py-4 text-sm text-gray-500">
                    No rates available for this package. Check dimensions, weight, and destination address.
                  </div>
                );
              }

              return (
                <div className="space-y-1.5 mt-3">
                  {rates.map((rate) => (
                    <button
                      key={rate.id}
                      type="button"
                      onClick={() => {
                        setPackageRates((prev) => ({
                          ...prev,
                          [currentWizardPackage.id]: {
                            ...prev[currentWizardPackage.id],
                            selectedRateId: rate.id
                          }
                        }));
                      }}
                      className={`w-full rounded-md border px-3 py-2.5 text-left transition-colors ${
                        selectedId === rate.id
                          ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            selectedId === rate.id ? 'border-indigo-500' : 'border-gray-300'
                          }`}>
                            {selectedId === rate.id && (
                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{rate.label}</div>
                            <div className="text-xs text-gray-500">
                              {rate.transitDays
                                ? `Est. ${rate.transitDays} business day${Number(rate.transitDays) === 1 ? '' : 's'}`
                                : 'Transit time unavailable'}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm font-semibold text-gray-900">${rate.cost.toFixed(2)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={wizardStep > 0 ? goPrevStep : cancelWizard}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800"
              >
                <LuChevronLeft className="h-4 w-4 mr-0.5" />
                {wizardStep > 0 ? 'Back' : 'Cancel'}
              </button>
              <div className="flex items-center gap-2">
                {unlabeledPackages.length > 1 && wizardStep < unlabeledPackages.length - 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      // Skip remaining packages and go to review
                      setWizardState('review');
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Skip to Review
                  </button>
                )}
                <button
                  type="button"
                  onClick={goNextStep}
                  disabled={!currentStepHasSelection}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {wizardStep < unlabeledPackages.length - 1 ? (
                    <>
                      Next Package
                      <LuChevronRight className="h-4 w-4 ml-0.5" />
                    </>
                  ) : (
                    'Review'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Review: summary of all selections */}
        {wizardState === 'review' && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Review Selections</h4>
              <button
                type="button"
                onClick={cancelWizard}
                className="text-gray-400 hover:text-gray-600"
              >
                <LuX className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              {unlabeledPackages.map((pkg, idx) => {
                const rate = getSelectedRateForPackage(pkg);
                return (
                  <div key={pkg.id} className="rounded-md border border-gray-200 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {pkg.package_label || `Package ${pkg.package_number}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          {pkg.weight_lbs || 0} lbs -- {pkg.length || '-'} x {pkg.width || '-'} x {pkg.height || '-'} in
                        </div>
                      </div>
                      {rate ? (
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900">${rate.cost.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{rate.label}</div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setWizardStep(idx);
                            if (packageRates[pkg.id]?.rates?.length > 0) {
                              setWizardState('selecting');
                            } else {
                              fetchRatesForPackage(pkg);
                            }
                          }}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          Select rate
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Already-labeled packages */}
              {packages.filter((pkg) => pkg.label_url).map((pkg) => (
                <div key={pkg.id} className="rounded-md border border-green-200 bg-green-50 px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-900">
                      {pkg.package_label || `Package ${pkg.package_number}`}
                    </div>
                    <span className="inline-flex items-center text-xs text-green-700 font-medium">
                      <LuCheck className="h-3 w-3 mr-0.5" />
                      Label purchased
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Total estimated</span>
              <span className="text-base font-semibold text-gray-900">${reviewTotal.toFixed(2)}</span>
            </div>

            {/* Review navigation */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  // Go back to the last package step
                  setWizardStep(unlabeledPackages.length - 1);
                  setWizardState('selecting');
                }}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800"
              >
                <LuChevronLeft className="h-4 w-4 mr-0.5" />
                Back
              </button>
              <button
                type="button"
                onClick={purchaseAllLabels}
                disabled={unlabeledPackages.some((pkg) => !packageRates[pkg.id]?.selectedRateId)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LuPackageCheck className="h-4 w-4 mr-1.5" />
                Purchase All Labels
              </button>
            </div>
          </div>
        )}

        {/* Purchasing: progress indicator */}
        {wizardState === 'purchasing' && (
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Purchasing Labels</h4>
            </div>
            <div className="py-4">
              <div className="flex items-center justify-center mb-3">
                <LoadingSpinner />
                <span className="text-sm font-medium text-indigo-700">
                  Purchasing {purchaseProgress.current} of {purchaseProgress.total}...
                </span>
              </div>
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${purchaseProgress.total > 0 ? (purchaseProgress.current / purchaseProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Done: success state */}
        {wizardState === 'done' && (
          <div className="pt-3 border-t border-gray-100">
            <div className="py-4 text-center">
              <div className="flex items-center justify-center mb-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <LuCheck className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-900">
                {purchaseErrors.length === 0
                  ? 'All labels purchased!'
                  : `${unlabeledPackages.length - purchaseErrors.length} of ${unlabeledPackages.length} labels purchased`}
              </p>
              {purchaseErrors.length > 0 && (
                <div className="mt-2 text-left">
                  {purchaseErrors.map((err, i) => (
                    <p key={i} className="text-xs text-red-600">{err}</p>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={cancelWizard}
                className="mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Refund Label */}
        {canRefundLabel && (
          <div className="pt-1">
            <button
              onClick={() => {
                if (window.confirm('Refund this shipping label? This may take 5-10 business days.')) {
                  refundLabelMutation.mutate();
                }
              }}
              disabled={refundLabelMutation.isLoading}
              className="text-xs text-red-600 hover:text-red-800"
            >
              {refundLabelMutation.isLoading ? 'Processing...' : 'Refund Label'}
            </button>
          </div>
        )}

        {/* Tracking Timeline */}
        {tracking?.tracking_details && tracking.tracking_details.length > 0 && (
          <div className="pt-3 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
              <LuMapPin className="h-3 w-3 mr-1" />
              Tracking Updates
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {tracking.tracking_details.slice(0, 10).map((detail, index) => (
                <div key={index} className="flex items-start space-x-2 text-xs">
                  <div className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    index === 0 ? 'bg-indigo-500' : 'bg-gray-300'
                  }`}></div>
                  <div>
                    <div className="text-gray-900">{detail.message}</div>
                    <div className="text-gray-400">
                      {detail.city && `${detail.city}${detail.state ? `, ${detail.state}` : ''} - `}
                      {new Date(detail.datetime).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Package Editor Modal */}
      <PackageEditor
        shipment={shipment}
        isOpen={packageEditorOpen}
        onClose={() => setPackageEditorOpen(false)}
        onSave={(updatedShipment) => {
          queryClient.invalidateQueries(['request']);
          onUpdate?.(updatedShipment);
        }}
      />
    </div>
  );
};

/**
 * Header shown at the top of each wizard step with package info and step counter.
 */
function WizardHeader({ step, total, pkg, onCancel }) {
  if (!pkg) return null;
  return (
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-medium text-indigo-600 mb-0.5">
          Package {step + 1} of {total}
        </div>
        <div className="text-sm font-medium text-gray-900">
          {pkg.package_label || `Package ${pkg.package_number}`}
        </div>
        <div className="text-xs text-gray-500">
          {pkg.package_type || 'box'} -- {pkg.weight_lbs || 0} lbs, {pkg.length || '-'} x {pkg.width || '-'} x {pkg.height || '-'} in
        </div>
        {pkg.PackageItems && pkg.PackageItems.length > 0 && (
          <div className="text-xs text-gray-500 mt-0.5">
            Contains: {pkg.PackageItems.map((item) => {
              const sku = item.InventoryItem?.product_sku || item.product_sku || 'item';
              return `${sku} x${item.quantity || 1}`;
            }).join(', ')}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="text-gray-400 hover:text-gray-600"
      >
        <LuX className="h-4 w-4" />
      </button>
    </div>
  );
}

export default ShipmentPanel;
